import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Crown,
  Gem,
  Heart,
  Instagram,
  MapPin,
  Sparkles,
  Stethoscope,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SEOHead } from "@/components/SEOHead";
import { faqSchema } from "@/lib/seo";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg, RoyalSectionHead } from "@/components/royal/shared";

const HERO_CATS = ["Skin", "Hair", "Face", "Body", "Wellness"];

const DIFFERENCE = [
  { icon: Stethoscope, title: "Doctor-led approach" },
  { icon: ClipboardList, title: "Personalised treatment plans" },
  { icon: Sparkles, title: "Advanced technology" },
  { icon: Heart, title: "Natural-looking results" },
  { icon: Gem, title: "Premium patient experience" },
];

const JOURNEY = [
  { n: "01", icon: Stethoscope, title: "Consultation", desc: "Understand your concerns and goals." },
  { n: "02", icon: ClipboardList, title: "Personalised Plan", desc: "Your doctor recommends the right approach." },
  { n: "03", icon: Crown, title: "Treatment & Follow-Up", desc: "Expert treatment with ongoing care." },
];

// Royal Signature home — xerox-faithful replication of the reference design.
export default function HomeRoyal() {
  const { content } = useContent();
  const { book } = useBooking();
  const s = content?.settings || {};
  const vis = (k) => s.section_visibility?.[k] ?? true;

  const treatments = content?.treatments || [];
  const six = treatments.slice(0, 6);
  const doctor = (content?.doctors || [])[0];
  const testimonials = (content?.testimonials || []).slice(0, 3);
  const gallery = content?.gallery || [];
  const comparisons = gallery.filter((g) => g.before_image && g.after_image);
  const posts = content?.instagram_posts || [];

  const heroImg = s.royal_hero_image || (treatments.find((t) => t.featured) || treatments[0])?.image || ROYAL_IMG.portrait;
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;
  // Hero background mode: "image" (single), "none" (clean, no photo), or
  // "rotating" (cycles through the doctors' portraits). Set in Settings → Homepage.
  const heroMode = s.royal_hero_mode || "image";
  const rotatingImgs = (content?.doctors || []).map((d) => d.image).filter(Boolean);
  const canRotate = heroMode === "rotating" && rotatingImgs.length > 0;
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (!canRotate) return undefined;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % rotatingImgs.length), 5000);
    return () => clearInterval(id);
  }, [canRotate, rotatingImgs.length]);
  const heroSrc = heroMode === "none" ? "" : canRotate ? rotatingImgs[heroIdx] : heroImg;
  const resultsRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollResults = (dir) => {
    const el = resultsRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 560), behavior: "smooth" });
  };

  const heroTitle = s.hero_title && s.hero_title !== "The Art Of Luxury" ? s.hero_title : "Where Science Meets Aesthetics";
  const heroWords = heroTitle.split(" ");
  const heroLine1 = heroWords.slice(0, 2).join(" ");
  const heroLine2 = heroWords.slice(2).join(" ");

  return (
    <div className="bg-[#FAF8F5]" data-testid="home-page">
      {/* HERO — full-bleed liquid background, ivory veil, cinematic drift */}
      <section className="relative flex min-h-[calc(100svh-76px)] items-center overflow-hidden bg-[#F1EBE2]" data-testid="hero-section">
        <RoyalLiquidBg src={heroSrc} blur={heroBlur} testid="hero-image" scrim />
        {/* category rail — right edge, over the scrim strip */}
        <nav className="absolute right-5 top-1/2 z-10 hidden -translate-y-1/2 flex-col items-end gap-4 lg:flex" aria-label="Treatment categories" data-testid="hero-category-rail">
          {HERO_CATS.map((c, i) => (
            <motion.div key={c} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 + i * 0.1, duration: 0.6 }}>
              <Link to="/treatments" data-testid={`hero-cat-${c.toLowerCase()}`} className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#2E2A26] transition-colors hover:text-[#C5A059]">
                {c}
              </Link>
            </motion.div>
          ))}
        </nav>

        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <div className="max-w-xl py-16 lg:py-24">
            <Reveal>
              <p className="eyebrow-royal" data-testid="hero-eyebrow">
                {s.hero_eyebrow || "Advanced Aesthetic Medicine"}
              </p>
            </Reveal>
            <h1 data-testid="hero-title" className="mt-6 font-serif text-5xl font-light leading-[1.05] tracking-tight text-[#0F0F0F] sm:text-6xl lg:text-7xl">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}>
                  {heroLine1}
                </motion.span>
              </span>
              {heroLine2 && (
                <span className="block overflow-hidden pb-2">
                  <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}>
                    {heroLine2}
                  </motion.span>
                </span>
              )}
            </h1>
            <Reveal delay={0.45}>
              <p className="mt-6 max-w-md text-sm font-normal leading-relaxed text-[#4A4A4A]" data-testid="hero-subtitle">
                {s.hero_subtitle || "Advanced skin, hair & facial aesthetics by Dr. Javeria Atif, Dubai."}
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-8 flex flex-wrap items-center gap-3.5">
                <button onClick={() => book()} data-testid="hero-book-now-button" className="btn-royal px-7 py-3.5 text-[10px]">
                  Book a Consultation
                </button>
                <Link to="/treatments" data-testid="hero-explore-treatments-link" className="btn-royal-outline px-7 py-3.5 text-[10px]">
                  Explore Treatments
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.8}>
              <p className="mt-16 hidden items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8580] lg:flex" aria-hidden="true">
                Scroll
                <motion.span
                  animate={{ scaleY: [1, 0.4, 1], opacity: [0.9, 0.4, 0.9] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                  className="block h-10 w-px origin-top bg-[#6B645C]"
                />
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY — image left, editorial right */}
      <section className="bg-white" data-testid="philosophy-section">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-24 lg:px-12 lg:py-24">
          <Reveal>
            <div className="overflow-hidden rounded-[1.5rem]">
              <img src={s.royal_philosophy_image || ROYAL_IMG.clinic} alt="Inside Royal Skin Clinic — reception lounge" loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="philosophy-image" />
            </div>
          </Reveal>
          <div>
            <RoyalSectionHead eyebrow="Our Philosophy" title="Enhancing what makes you, you." testid="philosophy" />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#4A4A4A]">
                At Royal Skin Clinic, every treatment is thoughtfully designed around your features, your skin and your
                individual goals. We believe in subtle, natural-looking results that bring out your best, not change who
                you are.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/about" data-testid="philosophy-learn-more" className="royal-link mt-8">
                Learn More <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SIGNATURE TREATMENTS — 6-up editorial card row */}
      {vis("home_treatments") && six.length > 0 && (
        <section className="bg-[#FAF8F5]" data-testid="royal-treatments-section">
          <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <RoyalSectionHead eyebrow="Signature Treatments" title="Our Treatments" testid="royal-treatments" />
              <Reveal delay={0.2}>
                <Link to="/treatments" data-testid="home-view-all-treatments-link" className="royal-link">
                  View All Treatments <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6" data-testid="home-treatments-grid">
              {six.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.07}>
                  <article className="group" data-testid={`home-treatment-card-${t.id}`}>
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src={t.image}
                        alt={t.name}
                        loading="lazy"
                        className="aspect-square w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0F0F0F]">{t.name}</h3>
                    <p className="mt-1.5 line-clamp-2 text-xs font-light leading-relaxed text-[#8A8580]">{t.tagline}</p>
                    <button
                      onClick={() => book(t.name)}
                      data-testid={`home-treatment-book-${t.id}`}
                      aria-label={`Book ${t.name}`}
                      className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B645C] transition-colors hover:text-[#C5A059]"
                    >
                      Book <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEET THE DOCTOR — beige band, landscape portrait */}
      {vis("home_doctors") && doctor && (
        <section className="bg-[#F1EBE2]" data-testid="royal-doctor-section">
          <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-24 lg:px-12 lg:py-24">
            <Reveal>
              <div className="overflow-hidden rounded-[1.5rem]">
                <img src={doctor.image} alt={`Portrait of ${doctor.name}`} loading="lazy" className="aspect-[4/3] w-full object-cover object-top" data-testid="royal-doctor-image" />
              </div>
            </Reveal>
            <div>
              <RoyalSectionHead eyebrow="Meet the Doctor" title={doctor.name} testid="royal-doctor" />
              <Reveal delay={0.15}>
                <p className="mt-4 text-sm font-normal leading-relaxed text-[#4A4A4A]">{doctor.role}.</p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-4 max-w-lg text-sm font-normal leading-relaxed text-[#4A4A4A]">{doctor.bio}</p>
              </Reveal>
              <Reveal delay={0.3}>
                <ul className="mt-8 grid max-w-lg gap-4 sm:grid-cols-2" data-testid="royal-doctor-checklist">
                  {(doctor.specializations || []).slice(0, 4).map((sp) => (
                    <li key={sp} className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.12em] text-[#0F0F0F]">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[5px] border border-[#E8E2D9] bg-white">
                        <BadgeCheck className="h-4 w-4 text-[#C5A059]" aria-hidden="true" />
                      </span>
                      {sp}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.4}>
                <Link to="/doctors" data-testid="royal-doctor-profile-link" className="royal-link mt-9">
                  View {doctor.name.match(/^Dr\.?\s*/i) ? doctor.name : `Dr. ${doctor.name}`}'s Profile <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* REAL RESULTS — before/after rail with side arrows */}
      {vis("home_gallery") && comparisons.length > 0 && (
        <section className="bg-white" data-testid="royal-results-section">
          <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <RoyalSectionHead eyebrow="Real Results" title="Real Results. Real Confidence." testid="royal-results" />
              <Reveal delay={0.2}>
                <Link to="/gallery" data-testid="home-view-all-results-link" className="royal-link">
                  View All Results <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Reveal>
            </div>
            <div className="relative mt-12">
              <button
                onClick={() => scrollResults(-1)}
                data-testid="results-prev"
                aria-label="Previous results"
                className="absolute -left-4 top-[38%] z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#E8E2D9] bg-white text-[#0F0F0F] shadow-md transition-colors hover:border-[#C5A059] hover:text-[#C5A059] lg:flex"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                onClick={() => scrollResults(1)}
                data-testid="results-next"
                aria-label="Next results"
                className="absolute -right-4 top-[38%] z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#E8E2D9] bg-white text-[#0F0F0F] shadow-md transition-colors hover:border-[#C5A059] hover:text-[#C5A059] lg:flex"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <div ref={resultsRef} className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="royal-results-track">
                {comparisons.map((g) => (
                  <Link to="/gallery" key={g.id} className="group w-[230px] shrink-0 snap-start" data-testid={`royal-result-${g.id}`}>
                    <div className="flex gap-1 overflow-hidden rounded-xl">
                      <div className="relative w-1/2">
                        <img src={g.before_image} alt={`${g.title} — before`} loading="lazy" className="aspect-[4/5] w-full object-cover" />
                        <span className="absolute bottom-2 left-2 rounded-sm bg-black/55 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] text-white">Before</span>
                      </div>
                      <div className="relative w-1/2">
                        <img src={g.after_image} alt={`${g.title} — after`} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <span className="absolute bottom-2 right-2 rounded-sm bg-[#D4AF37] px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] text-[#0F0F0F]">After</span>
                      </div>
                    </div>
                    <p className="mt-3 text-[12px] font-semibold text-[#0F0F0F]">{g.title}</p>
                    <p className="mt-0.5 text-[10px] font-light uppercase tracking-[0.15em] text-[#8A8580]">{g.category}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* THE ROYAL DIFFERENCE — botanical panel + 5-up icon row */}
      <section className="bg-[#FAF8F5]" data-testid="royal-difference-section">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-24 lg:px-12 lg:py-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-[1.5rem]">
              <img src={s.royal_difference_image || ROYAL_IMG.botanical} alt="Royal Skin Clinic — calm botanical interior" loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="royal-difference-image" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                {s.brand_lockup_url ? (
                  <img src={s.brand_lockup_url} alt="Royal Skin Clinic" className="w-40 drop-shadow-xl" />
                ) : (
                  <p className="text-center leading-tight">
                    <span className="block font-serif text-2xl font-medium uppercase tracking-[0.3em] text-white drop-shadow-lg">Royal</span>
                    <span className="mt-1.5 block text-[9px] font-semibold uppercase tracking-[0.45em] text-white/85 drop-shadow">Skin Clinic</span>
                  </p>
                )}
              </div>
            </div>
          </Reveal>
          <div>
            <RoyalSectionHead eyebrow="Why Royal Skin Clinic" title="The Royal Difference" testid="royal-difference" />
            <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-9 sm:grid-cols-3 lg:grid-cols-5" data-testid="royal-difference-grid">
              {DIFFERENCE.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.08}>
                  <div>
                    <f.icon className="h-6 w-6 text-[#0F0F0F]" strokeWidth={1.25} aria-hidden="true" />
                    <p className="mt-3 text-[12px] font-medium leading-snug text-[#0F0F0F]">{f.title}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TREATMENT JOURNEY — numbered steps + side image */}
      <section className="bg-white" data-testid="royal-journey-section">
        <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <RoyalSectionHead eyebrow="Your Treatment Journey" title="A simple, supportive process." testid="royal-journey" />
            <Reveal delay={0.2}>
              <button onClick={() => book()} data-testid="royal-journey-book-link" className="royal-link">
                Book Your Consultation <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-10 md:grid-cols-3 lg:grid-cols-4" data-testid="royal-journey-steps">
            {JOURNEY.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <div>
                  <span className="flex h-11 w-11 items-center justify-center rounded-[8px] border border-[#E8E2D9] bg-[#FAF8F5]">
                    <step.icon className="h-5 w-5 text-[#0F0F0F]" strokeWidth={1.5} aria-hidden="true" />
                  </span>
                  <p className="mt-5 text-[11px] font-semibold tracking-[0.2em] text-[#C5A059]">{step.n}</p>
                  <h3 className="mt-1.5 font-serif text-xl font-normal text-[#0F0F0F]">{step.title}</h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-[#8A8580]">{step.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="hidden lg:block">
              <div className="overflow-hidden rounded-xl">
                <img src={s.royal_journey_image || ROYAL_IMG.clinic} alt="Treatment suite at Royal Skin Clinic" loading="lazy" className="aspect-[4/5] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — quote cards with Google Review tag */}
      {vis("home_testimonials") && testimonials.length > 0 && (
        <section className="bg-[#FAF8F5]" data-testid="royal-testimonials-section">
          <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <RoyalSectionHead eyebrow="What Our Patients Say" title="Trusted by Thousands" testid="royal-testimonials" />
            <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="royal-testimonials-grid">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.1}>
                  <figure className="h-full rounded-xl border border-[#E8E2D9] bg-white p-7" data-testid={`testimonial-card-${t.id}`}>
                    <blockquote className="text-sm font-light italic leading-relaxed text-[#4A4A4A]">“{t.text}”</blockquote>
                    <figcaption className="mt-6">
                      <p className="text-xs font-semibold text-[#0F0F0F]">— {t.name}</p>
                      <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[#8A8580]">
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#F8F3E6] text-[9px] font-bold text-[#C5A059]">G</span>
                        Google Review
                      </p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — accordion with auto FAQPage schema */}
      {vis("home_faq") && (content?.faqs || []).length > 0 && (
        <section className="bg-white" data-testid="faq-section">
          <SEOHead jsonLd={[faqSchema(content.faqs)]} />
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8 lg:py-24">
            <RoyalSectionHead eyebrow="Questions, Answered" title="Everything you're wondering" testid="faq" />
            <div className="mt-12 divide-y divide-[#E8E2D9] border-y border-[#E8E2D9]" data-testid="faq-list">
              {content.faqs.map((f, i) => (
                <div key={f.id}>
                  <button
                    onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                    data-testid={`faq-question-${f.id}`}
                    aria-expanded={openFaq === f.id}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left transition-colors hover:text-[#C5A059]"
                  >
                    <span className="font-serif text-lg font-normal text-[#0F0F0F] sm:text-xl">{f.question}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-[#C5A059] transition-transform duration-500 ease-liquid ${openFaq === f.id ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === f.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-7 pr-10 text-sm font-light leading-relaxed text-[#4A4A4A]" data-testid={`faq-answer-${f.id}`}>
                          {f.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM STRIP */}
      {posts.length > 0 && (s.section_visibility?.home_instagram ?? true) && (
        <section className="bg-white" data-testid="instagram-section">
          <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <RoyalSectionHead eyebrow="Follow the Royal Experience" title="@royalskinclinic" testid="royal-instagram" />
              <Reveal delay={0.2}>
                <a
                  href={s.instagram || "https://www.instagram.com/royalskincosmeticclinic/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-follow-link"
                  className="btn-royal-outline px-6 py-3 text-[10px]"
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" /> Follow Us on Instagram
                </a>
              </Reveal>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="instagram-grid">
              {posts.slice(0, 6).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <a
                    href={p.link || s.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`instagram-post-${p.id}`}
                    className="group relative block overflow-hidden rounded-sm"
                    aria-label={p.caption || "Instagram post"}
                  >
                    <img src={p.image} alt={p.caption || "Royal Skin Clinic Instagram post"} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-110" />
                    <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F0F]/55 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <Instagram className="h-6 w-6 text-white" aria-hidden="true" />
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <RoyalCTA />
    </div>
  );
}
